import { useEffect, useState } from "react";
import { Loader2, LayoutGrid, Ruler, Package, Sparkles, FileText, CalendarCheck, ShieldCheck, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import { SaveBar } from "@/components/admin/fields";
import RoomRates from "@/components/admin/RoomRates";
import PackagesAdmin from "@/components/admin/PackagesAdmin";
import AddonsAdmin from "@/components/admin/AddonsAdmin";
import QuotesAdmin from "@/components/admin/QuotesAdmin";
import BookingsAdmin from "@/components/admin/BookingsAdmin";
import HistoryAdmin from "@/components/admin/HistoryAdmin";
import DraftPreview from "@/components/admin/DraftPreview";

const TABS = [
  ["overview", "Overview", LayoutGrid], ["rooms", "Room Rates", Ruler], ["packages", "Packages", Package],
  ["addons", "Add-ons", Sparkles], ["quotes", "Quotations", FileText], ["bookings", "Bookings", CalendarCheck], ["history", "History", History],
];

function AdminLogin() {
  const { login, logout, user } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const u = await login(f.email, f.password); if (u.role !== "admin") { setErr("This account is not a staff account."); await logout(); } }
    catch (ex) { setErr(formatErr(ex)); }
    setBusy(false);
  };
  return (
    <div className="min-h-[70vh] grid place-items-center px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-brand-line bg-brand-cream p-8" data-testid="admin-login-form">
        <p className="text-xs uppercase tracking-[0.25em] text-brand-accent inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Staff access</p>
        <h1 className="font-serif-display text-4xl text-brand-ink mt-3">Admin panel</h1>
        {user && user.role !== "admin" && <p className="text-xs text-brand-muted mt-2">Signed in as {user.email} (client). Sign in with the staff account below.</p>}
        <div className="space-y-4 mt-8">
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 bg-white" data-testid="admin-email-input" /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11 bg-white" data-testid="admin-password-input" /></div>
        </div>
        {err && <p className="text-sm text-red-700 mt-4" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} className="mt-6 w-full rounded-full bg-brand-ink hover:bg-brand-accent text-brand-cream py-3 text-sm transition-colors inline-flex justify-center items-center gap-2" data-testid="admin-login-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
        </button>
      </form>
    </div>
  );
}

function Overview({ stats, go }) {
  const cards = stats ? [["New bookings", stats.bookings_new, "bookings"], ["Quotations today", stats.quotes_today, "quotes"], ["Total quotations", stats.quotes_total, "quotes"], ["Client accounts", stats.users_total, null]] : [];
  return (
    <div data-testid="admin-overview">
      <h2 className="font-serif-display text-3xl text-brand-ink mb-6">Today at homiin</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([l, v, tab]) => (
          <button key={l} onClick={() => tab && go(tab)} className="text-left rounded-2xl border border-brand-line bg-brand-cream p-6 hover:border-brand-accent transition-colors" data-testid={`stat-${l.replace(/\s/g, "-").toLowerCase()}`}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-brand-muted-light">{l}</p>
            <p className="font-serif-display text-5xl text-brand-ink mt-2">{v}</p>
          </button>
        ))}
      </div>
      <p className="text-sm text-brand-muted mt-8 max-w-xl leading-relaxed">Edit the rate card in <b>Room Rates</b>, <b>Packages</b> and <b>Add-ons</b>. Saved prices apply to the very next quotation; past quotations keep the prices they were generated with.</p>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [rc, setRc] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user && user.role === "admin";

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    if (!isAdmin) return;
    loadStats();
    api.get("/admin/rate-card").then((r) => { setRc(r.data); setSaved(JSON.stringify(r.data)); }).catch((e) => toast.error(formatErr(e)));
  }, [isAdmin]);

  if (user === null) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>;
  if (!isAdmin) return <AdminLogin />;

  const dirty = rc && saved !== JSON.stringify(rc);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/rate-card", rc); setRc(data); setSaved(JSON.stringify(data)); toast.success("Rate card saved — live for new quotations"); }
    catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };
  const discard = () => setRc(JSON.parse(saved));
  const reset = async () => {
    if (!window.confirm("Reset the entire rate card to homiin defaults?")) return;
    try { const { data } = await api.post("/admin/rate-card/reset"); setRc(data); setSaved(JSON.stringify(data)); toast.success("Rate card reset to defaults"); } catch (e) { toast.error(formatErr(e)); }
  };
  const editing = ["rooms", "packages", "addons"].includes(tab);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-[220px_1fr] gap-10" data-testid="admin-page">
      <aside className="lg:sticky lg:top-24 self-start">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-brand-accent mb-6"><ShieldCheck className="w-4 h-4" /> Staff panel</div>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`inline-flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm whitespace-nowrap transition-colors ${tab === k ? "bg-brand-ink text-brand-cream" : "text-brand-muted hover:bg-brand-sand hover:text-brand-ink"}`}>
              <Icon className="w-4 h-4" /> {l}{k === "bookings" && stats?.bookings_new ? <span className="ml-auto text-[10px] bg-brand-accent text-white rounded-full px-2 py-0.5" data-testid="admin-new-bookings-badge">{stats.bookings_new}</span> : null}
            </button>
          ))}
        </nav>
        <div className="mt-8 pt-6 border-t border-brand-line text-xs text-brand-muted space-y-1">
          <p className="truncate">{user.email}</p>
          <button onClick={reset} className="block inline-flex items-center gap-1 text-brand-muted hover:text-brand-ink" data-testid="admin-reset-defaults-btn"><RotateCcw className="w-3 h-3" /> Reset rate card to defaults</button>
          <button onClick={logout} className="block text-brand-accent hover:text-brand-accent-dark" data-testid="admin-logout-btn">Sign out</button>
        </div>
      </aside>
      <div className="min-w-0">
        {tab === "overview" && <Overview stats={stats} go={setTab} />}
        {editing && !rc && <Loader2 className="w-6 h-6 animate-spin text-brand-accent" />}
        {tab === "rooms" && rc && <RoomRates rc={rc} setRc={setRc} />}
        {tab === "packages" && rc && <PackagesAdmin rc={rc} setRc={setRc} />}
        {tab === "addons" && rc && <AddonsAdmin rc={rc} setRc={setRc} />}
        {tab === "quotes" && <QuotesAdmin />}
        {tab === "bookings" && <BookingsAdmin onChange={loadStats} />}
        {tab === "history" && <HistoryAdmin key={saved} />}
        {editing && rc && <SaveBar dirty={dirty} saving={saving} onSave={save} onReset={dirty ? discard : null}><DraftPreview rc={rc} /></SaveBar>}
      </div>
    </div>
  );
}
